/**
 * LocalStorage-черновик NewProjectWizard.
 *
 * Зеркалит subset `WizardState`. Мастер двухшаговый («Шаблон» → «Параметры»);
 * создание атомарно одним действием, превью-шага и `simulationId` в черновике
 * нет. Authoritative state — React.
 */
const STORAGE_KEY = "gudwin:new-project-wizard:draft:v7";
const PREVIOUS_STORAGE_KEY = "gudwin:new-project-wizard:draft:v6";
const LEGACY_STORAGE_KEYS = [
  "gudwin:new-project-wizard:draft:v5",
  "gudwin:new-project-wizard:draft:v4",
  "gudwin:new-project-wizard:draft:v3",
  "gudwin:new-project-wizard:draft:v2",
  "gudwin:new-project-wizard:draft:v1",
];

export interface WizardDraftPayload {
  step: "template" | "params";
  selectedPk: number | null;
  projectName: string;
  code: string;
  customerName: string;
  plannedStart: string;
  plannedEnd: string;
  calendarId: number | null;
  managerUserId: number | null;
  equipmentTypeId: number | null;
  equipmentQuantity: number;
  parameters: Record<string, string>;
}

function isPayload(v: unknown): v is WizardDraftPayload {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    (o.step === "template" || o.step === "params") &&
    (o.selectedPk === null || typeof o.selectedPk === "number") &&
    typeof o.projectName === "string" &&
    typeof o.code === "string" &&
    typeof o.customerName === "string" &&
    typeof o.plannedStart === "string" &&
    typeof o.plannedEnd === "string" &&
    (o.calendarId === null || typeof o.calendarId === "number") &&
    (o.managerUserId === null || typeof o.managerUserId === "number") &&
    (o.equipmentTypeId === null || typeof o.equipmentTypeId === "number") &&
    typeof o.equipmentQuantity === "number" &&
    Number.isInteger(o.equipmentQuantity) &&
    o.equipmentQuantity >= 1 &&
    typeof o.parameters === "object" &&
    o.parameters !== null
  );
}

export function loadDraft(): WizardDraftPayload | null {
  if (typeof window === "undefined") return null;
  try {
    // Best-effort cleanup устаревших ключей (pre-prod, обратной совместимости нет).
    for (const key of LEGACY_STORAGE_KEYS) window.localStorage.removeItem(key);
    const raw =
      window.localStorage.getItem(STORAGE_KEY) ?? window.localStorage.getItem(PREVIOUS_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const normalized = {
      ...(parsed as Record<string, unknown>),
      equipmentQuantity:
        typeof (parsed as Record<string, unknown>).equipmentQuantity === "number"
          ? (parsed as Record<string, unknown>).equipmentQuantity
          : 1,
    };
    window.localStorage.removeItem(PREVIOUS_STORAGE_KEY);
    return isPayload(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

export function saveDraft(payload: WizardDraftPayload): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage может быть отключён / переполнен — молча игнорируем.
  }
}

export function clearDraft(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // см. saveDraft
  }
}

export function isEmptyDraft(payload: WizardDraftPayload): boolean {
  return (
    payload.selectedPk === null &&
    payload.projectName === "" &&
    payload.code === "" &&
    payload.customerName === "" &&
    payload.plannedStart === "" &&
    payload.plannedEnd === "" &&
    payload.calendarId === null &&
    payload.managerUserId === null &&
    payload.equipmentTypeId === null &&
    payload.equipmentQuantity === 1 &&
    Object.keys(payload.parameters).length === 0
  );
}
